import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {getUser} from '../../auth/jwtAuth';
import {fetchKbEntitySummary} from '../../utils/api/entities';
import {isLinkedEntityRef} from '../../utils/entityLinks';
import {getEntityTypeLabel, isPlaceholderEntityType} from '../../utils/entityTypeMeta';
import {useMentionHoverRefs} from './MentionHoverDismissContext';
import {shouldSuppressEntityHover} from './entityHoverSuppression';

/**
 * Normalize a confidence value into an integer percentage (0–100), or null when absent.
 * Accepts a fraction in [0,1] (the corpus/NER convention) or an already-percent value (>1).
 */
export function normalizeConfidencePercent(confidence) {
    if (confidence == null || confidence === '') return null;
    const n = Number(confidence);
    if (!Number.isFinite(n)) return null;
    const pct = n <= 1 ? n * 100 : n;
    if (pct < 0) return null;
    return Math.round(Math.min(pct, 100));
}

/**
 * Whether the current session belongs to an admin. Reads the synchronous in-memory user from
 * jwtAuth so it works both inside React (where UserProvider context exists) and in detached
 * React roots like the ProseMirror hover plugin (which render outside the provider tree).
 * The NER/NEL confidence scores are an internal model-quality signal, shown to admins only.
 */
export function isAdminUser() {
    const user = getUser();
    if (!user) return false;
    return Boolean(
        user.isAdmin
        || user.role === 'admin'
        || (Array.isArray(user.roles) && user.roles.includes('admin'))
    );
}

const TYPE_CHIP_SX = {
    fontSize: 11,
    fontWeight: 600,
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
    lineHeight: 1.4,
};

const KB_TYPE_CHIP_SX = {
    ...TYPE_CHIP_SX,
    bgcolor: 'info.main',
};

const TYPE_CHIP_LABEL_SX = {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3,
    opacity: 0.85,
    mr: 0.35,
};

// Compact, labelled score chip. NER (mention detection) and NEL (entity linking) are shown as
// two separate chips so an admin can read each model's confidence independently at a glance.
const SCORE_CHIP_SX = {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 0.5,
    fontSize: 11,
    fontWeight: 600,
    bgcolor: 'rgba(0,0,0,0.06)',
    color: 'text.secondary',
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
    lineHeight: 1.4,
    fontVariantNumeric: 'tabular-nums',
};

const SCORE_LABEL_SX = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    color: 'text.disabled',
};

const SCORE_VALUE_SX = {
    fontSize: 11,
    fontWeight: 700,
    color: 'text.primary',
};

/**
 * A single compact score chip: a short label (NER/NEL) followed by its percentage.
 */
function ScoreChip({label, percent, title}) {
    return (
        <Box component="span" sx={SCORE_CHIP_SX} title={title}>
            <Box component="span" sx={SCORE_LABEL_SX}>{label}</Box>
            <Box component="span" sx={SCORE_VALUE_SX}>{percent}%</Box>
        </Box>
    );
}

/**
 * Shared hover popup content for an entity mention.
 * Shows the mention name and its entity type to everyone; the NER / NEL confidence scores are
 * an internal model-quality signal and are rendered for admins only.
 *
 * Two distinct scores may be shown (admins only):
 *  - NER (nerScore): confidence that the span is a real entity mention.
 *  - NEL (linkingScore): confidence in the linked Wikidata QID.
 *
 * The legacy single `confidence` prop is treated as the NER score when `nerScore` is not
 * supplied, so older callers keep working. Scores are omitted gracefully when absent.
 *
 * @param {boolean} [isAdmin] Override admin detection (defaults to the live session via isAdminUser()).
 * @param {string|null} [kbEntityType] Authoritative KB entity type (shown for linked entities).
 */
export function MentionHoverContent({name, entityType, kbEntityType, confidence, nerScore, linkingScore, isAdmin}) {
    const admin = isAdmin != null ? isAdmin : isAdminUser();
    const nerPct = normalizeConfidencePercent(nerScore != null ? nerScore : confidence);
    const nelPct = normalizeConfidencePercent(linkingScore);
    const showScores = admin && (nerPct != null || nelPct != null);
    const nerTypeLabel = entityType && !isPlaceholderEntityType(entityType)
        ? getEntityTypeLabel(entityType)
        : null;
    const kbTypeLabel = kbEntityType && !isPlaceholderEntityType(kbEntityType)
        ? getEntityTypeLabel(kbEntityType)
        : null;
    const showKbType = Boolean(kbTypeLabel) && kbTypeLabel !== nerTypeLabel;

    return (
        <Stack spacing={0.75} sx={{py: 0.5, px: 0.25, minWidth: 160, maxWidth: 280}}>
            {name && (
                <Typography variant="subtitle2" sx={{fontWeight: 600, lineHeight: 1.2}}>
                    {name}
                </Typography>
            )}
            {(nerTypeLabel || showKbType || kbTypeLabel) && (
                <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center'}}>
                    {nerTypeLabel && (
                        <Box component="span" sx={TYPE_CHIP_SX} title="NER-predicted entity type">
                            <Box component="span" sx={TYPE_CHIP_LABEL_SX}>NER</Box>
                            {nerTypeLabel}
                        </Box>
                    )}
                    {(showKbType || (!nerTypeLabel && kbTypeLabel)) && (
                        <Box component="span" sx={KB_TYPE_CHIP_SX} title="Knowledge base entity type">
                            <Box component="span" sx={TYPE_CHIP_LABEL_SX}>KB</Box>
                            {kbTypeLabel}
                        </Box>
                    )}
                </Box>
            )}
            {showScores && (
                <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center'}}>
                    {nerPct != null && (
                        <ScoreChip label="NER" percent={nerPct} title="Mention-detection confidence (NER)" />
                    )}
                    {nelPct != null && (
                        <ScoreChip label="NEL" percent={nelPct} title="Entity-linking confidence (NEL)" />
                    )}
                </Box>
            )}
        </Stack>
    );
}

/**
 * Wraps a mention span with a hover tooltip showing name + type for everyone and the NER / NEL
 * confidence scores for admins only. Used identically by the report, data-items, and corpus
 * mention renderers so hover behavior is consistent everywhere. When no metadata is available,
 * renders children without a tooltip wrapper.
 *
 * Hover is dismissed on mention click via {@link MentionHoverDismissProvider} — wrap each
 * document viewer in that provider so the suppress flag is shared (one state per pane).
 *
 * @param {boolean} [isAdmin] Override admin detection (defaults to the live session via isAdminUser()).
 */
export default function MentionHoverCard({
    name,
    entityType,
    kbEntityType: kbEntityTypeProp = null,
    entityRef = null,
    confidence,
    nerScore,
    linkingScore,
    children,
    enterDelay = 400,
    isAdmin,
    mentionAnchorEl = null,
    // PERF: Prefer passing a React ref object rather than a DOM element extracted via setState.
    // The DOM element is only needed at hover-check time (not at render time), so resolving
    // ref.current lazily avoids the setState-in-ref-callback render storm that occurs when
    // thousands of entity marks mount simultaneously on large NER-annotated documents.
    mentionAnchorRef = null,
    mentionUid = null,
    suppressHover = false,
}) {
    const admin = isAdmin != null ? isAdmin : isAdminUser();
    const [kbEntityTypeFetched, setKbEntityTypeFetched] = useState(null);
    const [kbDisplayNameFetched, setKbDisplayNameFetched] = useState(null);
    const linkedEntityRef = entityRef && isLinkedEntityRef(entityRef) ? entityRef.trim() : null;
    const kbEntityType = kbEntityTypeProp || kbEntityTypeFetched;
    const hasScore = admin && (
        normalizeConfidencePercent(nerScore != null ? nerScore : confidence) != null
        || normalizeConfidencePercent(linkingScore) != null
    );
    const hasNerType = Boolean(entityType) && !isPlaceholderEntityType(entityType);
    const hasKbType = Boolean(kbEntityType) && !isPlaceholderEntityType(kbEntityType);
    const hasMeta = Boolean(name || hasNerType || hasKbType) || hasScore;
    const hoverRefs = useMentionHoverRefs();

    const isHoverBlockedForMention = useCallback(() => {
        if (suppressHover) return true;
        if (hoverRefs?.transientSuppress) return true;
        // Resolve lazily: prefer the ref object (no re-render on mount) over the DOM element.
        const resolvedAnchorEl = mentionAnchorRef ? mentionAnchorRef.current : mentionAnchorEl;
        const hoverTarget = {
            anchorEl: resolvedAnchorEl,
            uid: mentionUid,
        };
        if (shouldSuppressEntityHover(hoverRefs?.activePopover || null, hoverTarget)) {
            return true;
        }
        return shouldSuppressEntityHover(hoverRefs?.pressedMention || null, hoverTarget);
    }, [suppressHover, hoverRefs, mentionAnchorEl, mentionAnchorRef, mentionUid]);

    // PDF extracted-text docs can carry thousands of NER marks. Mounting a MUI Tooltip per
    // mention on first paint freezes the pane (white screen). Arm the Tooltip only after hover.
    const [tooltipArmed, setTooltipArmed] = useState(false);
    // The armed Tooltip is FULLY controlled via this flag. MUI decides controlled-vs-uncontrolled
    // once at first mount, so an armed Tooltip mounted with `open={true}` can never be handed
    // back to MUI's own hover handling — removing the prop later just leaves it pinned open
    // (the "tooltip never disappears" bug). Instead we keep it controlled forever and close it
    // ourselves on the wrapper's mouseleave.
    const [open, setOpen] = useState(false);
    const disarmTimerRef = useRef(null);
    const enterTimerRef = useRef(null);
    useEffect(() => () => {
        if (disarmTimerRef.current) clearTimeout(disarmTimerRef.current);
        if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
    }, []);

    const dismissTooltip = useCallback(() => {
        if (enterTimerRef.current) {
            clearTimeout(enterTimerRef.current);
            enterTimerRef.current = null;
        }
        setOpen(false);
        if (disarmTimerRef.current) clearTimeout(disarmTimerRef.current);
        disarmTimerRef.current = setTimeout(() => setTooltipArmed(false), 50);
    }, []);

    /** Close the visible tooltip without disarming — keeps the wrapper mounted so the click still lands. */
    const closeTooltipOnPress = useCallback(() => {
        if (enterTimerRef.current) {
            clearTimeout(enterTimerRef.current);
            enterTimerRef.current = null;
        }
        setOpen(false);
    }, []);

    const handleMouseDown = useCallback(() => {
        // Hide hover on press but do not disarm yet — disarming remounts the wrapper and can
        // swallow the click that should open the entity dialog (preview read-only viewers).
        closeTooltipOnPress();
    }, [closeTooltipOnPress]);

    useEffect(() => {
        if (suppressHover && (open || tooltipArmed)) {
            dismissTooltip();
        }
    }, [suppressHover, open, tooltipArmed, dismissTooltip]);

    // When the tooltip is open and the scroll container moves, browser does NOT fire mouseleave
    // on elements that scroll away from the cursor. Without this, tooltips stay pinned open
    // indefinitely as the user scrolls through a dense NER document (PDF extracted text,
    // search results, etc.) — each hovered entity leaves a ghost tooltip on screen.
    useEffect(() => {
        if (!open) return;
        const closeOnScroll = () => {
            setOpen(false);
            if (disarmTimerRef.current) clearTimeout(disarmTimerRef.current);
            if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
            setTooltipArmed(false);
        };
        document.addEventListener('scroll', closeOnScroll, {capture: true, passive: true});
        return () => document.removeEventListener('scroll', closeOnScroll, true);
    }, [open]);

    useEffect(() => {
        setKbEntityTypeFetched(null);
        setKbDisplayNameFetched(null);
    }, [linkedEntityRef]);

    useEffect(() => {
        if (!tooltipArmed || !linkedEntityRef) return undefined;
        let cancelled = false;
        fetchKbEntitySummary(linkedEntityRef)
            .then((summary) => {
                if (cancelled || !summary) return;
                if (!kbEntityTypeProp && summary.entityType) {
                    setKbEntityTypeFetched(summary.entityType);
                }
                if (summary.canonicalName) {
                    setKbDisplayNameFetched(summary.canonicalName);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [tooltipArmed, linkedEntityRef, kbEntityTypeProp]);

    const displayName = kbDisplayNameFetched || name;

    const armTooltip = useCallback(() => {
        if (isHoverBlockedForMention()) return;
        if (disarmTimerRef.current) clearTimeout(disarmTimerRef.current);
        if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
        enterTimerRef.current = setTimeout(() => {
            setTooltipArmed(true);
            setOpen(true);
        }, enterDelay);
    }, [isHoverBlockedForMention, enterDelay]);

    const handleMouseEnter = useCallback(() => {
        if (isHoverBlockedForMention()) {
            dismissTooltip();
            return;
        }
        armTooltip();
    }, [isHoverBlockedForMention, dismissTooltip, armTooltip]);

    const releaseTooltip = useCallback(() => {
        // Cancel a pending enter if the mouse left before the delay fired
        if (enterTimerRef.current) {
            clearTimeout(enterTimerRef.current);
            enterTimerRef.current = null;
        }
        setOpen(false);
        // Fully disarm after the exit transition so long documents drop the popper again
        // and the next hover re-arms from scratch. Exit is now 0ms so 50ms is enough buffer.
        if (disarmTimerRef.current) clearTimeout(disarmTimerRef.current);
        disarmTimerRef.current = setTimeout(() => setTooltipArmed(false), 50);
    }, []);

    if (!hasMeta) return children;

    const hoverContent = (
        <MentionHoverContent
            name={displayName}
            entityType={entityType}
            kbEntityType={kbEntityType}
            confidence={confidence}
            nerScore={nerScore}
            linkingScore={linkingScore}
            isAdmin={admin}
        />
    );

    if (!tooltipArmed) {
        return (
            <Box
                component="span"
                data-mention-hover
                sx={{display: 'inline'}}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={releaseTooltip}
            >
                {children}
            </Box>
        );
    }

    return (
        <Tooltip
            arrow
            open={open && !isHoverBlockedForMention()}
            // pointer-events: none on the popup — it must never swallow the pointer, otherwise
            // hovering "into" it keeps the wrapper's mouseleave from closing it cleanly.
            disableInteractive
            placement="top"
            title={hoverContent}
            // Exit must be instant (0ms). Without this, MUI's default ~225ms Grow animation
            // keeps every tooltip visible while it fades out. When the mouse sweeps across
            // many entity spans quickly, multiple tooltips pile up on screen simultaneously
            // — each waiting out its own exit transition before disappearing.
            TransitionProps={{timeout: {enter: 150, exit: 0}}}
            componentsProps={{tooltip: {sx: {bgcolor: 'background.paper', color: 'text.primary', boxShadow: 3, p: 1}}}}
        >
            <Box
                component="span"
                data-mention-hover
                sx={{display: 'inline'}}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={releaseTooltip}
            >
                {children}
            </Box>
        </Tooltip>
    );
}
